import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  continueConversation,
  enableNexus,
  regenerateLastResponse,
  startConversation,
} from '@actions/nexus';
import { ID } from '@app/types';
import styles from './Prompt.module.scss';
import NexusLoadingBar from '../LoadingBar/LoadingBar';
import { useLocation } from 'react-router-dom';
import {
  NexusServiceStatus,
  NexusStatus,
  selectConversation,
  selectEnablingNexus,
  selectEnablingNexusFailed,
  selectNexusServiceStatus,
} from '@reducers/data/nexus';
import { ArrowRightIcon, LoaderIcon, RegenerateIcon, SubmitIcon } from '@components/Icons';
import { selectContact } from '@actions/contacts';
import Tooltip, { Overlay } from '@components/Tooltip';
import Contact from '@models/Contact';
import { Conversation } from '@models/nexus';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { supportHref } from '@support/preferred-email-client';

const getPlaceholder = (
  conversationTarget: Contact,
  conversationTargetContactID: ID,
  conversation: Conversation,
): string => {
  let placeholder = 'Ask Nexus a question...';

  if (conversationTargetContactID && conversationTarget) {
    placeholder = `Ask Nexus a question about ${conversationTarget.name}...`;
  }

  if (conversation.paywalled) {
    placeholder = '';
  }

  return placeholder;
};

// type UserState = 'freePlan' | 'nexusDisabled' | 'nexusEnabled' | 'refreshing';
type UserState = 'enablementIsDown' | 'nexusDisabled' | 'nexusEnabled' | 'refreshing';
const getUserState = (
  nexusStatus: NexusStatus,
  nexusServiceStatus: NexusServiceStatus,
): UserState => {
  if (nexusStatus.status !== 'ready' && nexusServiceStatus.is_processing_under_load) {
    return 'enablementIsDown';
  } else if (nexusStatus.status === 'processing') {
    return 'refreshing';
  } else if (nexusStatus.needs_permission) {
    return 'nexusDisabled';
  }
  // return isPaywalled ? 'freePlan' : 'nexusEnabled';
  return 'nexusEnabled';
};

export default function ChatPrompt({
  conversationID,
  conversationTargetContactID,
  // isPaywalled,
  nexusStatus,
}: {
  conversationID?: ID;
  conversationTargetContactID?: ID;
  // isPaywalled: boolean;
  nexusStatus: NexusStatus;
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string>('');
  const nexusServiceStatus = useSelector(selectNexusServiceStatus);
  const conversation = useSelector(selectConversation(conversationID));

  // const upgradeURL =
  //   useSelector(selectUpgradeURL) || supportHref({ subject: 'Upgrade to Pro plan' });
  const currentlyEnabling = useSelector(selectEnablingNexus);
  const enablementFailed = useSelector(selectEnablingNexusFailed);
  const loading = conversation.isFetching;
  const conversationTarget = useSelector(
    selectContact(conversation.targetContactID || conversationTargetContactID),
  );
  const shouldShowRegenerateBtn =
    conversationID && !loading && message === '' && conversation.messages.last()?.terminated;

  const placeholder = getPlaceholder(
    conversationTarget,
    conversation.targetContactID || conversationTargetContactID,
    conversation,
  );
  const userState = getUserState(nexusStatus, nexusServiceStatus);

  const enable = (updateUIOnSuccess: boolean) => {
    dispatch(enableNexus({ updateUIOnSuccess }));
  };

  // const handleUpgradeLinkClick = (e) => {
  //   e.preventDefault();
  //   if (nexusStatus.needs_permission) {
  //     enable(false);
  //   }

  //   window.open(upgradeURL, '_blank');
  // };

  const handleEnableNexusClick = () => {
    if (!currentlyEnabling) {
      enable(true);
    }
  };

  const sendMessage = () => {
    if (conversation.paywalled) {
      return;
    }

    if (!conversationID) {
      dispatch(
        startConversation(message, conversationTargetContactID || conversation.targetContactID),
      );
    } else {
      dispatch(continueConversation(conversationID, message, conversation.targetContactID));
    }

    setMessage('');
  };

  const submitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && message.length > 0) {
      sendMessage();
    }
  };

  const submitOnClick = () => {
    if (message.length > 0 && !loading) {
      sendMessage();
    }
  };

  useEffect(() => {
    if (inputRef.current && location.pathname !== '/nexus') {
      inputRef.current.focus();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (inputRef.current && !loading && location.pathname !== '/nexus') {
      inputRef.current.focus();
    }
  }, [loading]);

  return (
    <div className={styles.prompt}>
      <div className={styles.background} />
      <NexusLoadingBar
        loading={loading || conversation.paywalled || nexusStatus.status === 'processing'}
      />
      <StateBasedFadeTransition
        state={userState}
        elementByStateMap={{
          enablementIsDown: (
            <div className={styles.upgradePrompt}>
              We're currently experiencing heavy demand for Nexus. Check back in a bit!
            </div>
          ),
          nexusEnabled: (
            <span>
              <input
                type="text"
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className={styles.input}
                placeholder={loading ? undefined : placeholder}
                autoFocus
                tabIndex={1}
                onKeyPress={submitOnEnter}
                disabled={loading || conversation.paywalled}
              />

              <Tooltip placement="top" overlay={<Overlay>Submit</Overlay>}>
                <SubmitIcon
                  className={cn(
                    styles.submitBtn,
                    message.length === 0 && styles.disabled,
                    (loading || message.length === 0) && styles.hidden,
                  )}
                  onClick={submitOnClick}
                />
              </Tooltip>
              <Tooltip placement="top" overlay={<Overlay>Regenerate</Overlay>}>
                <RegenerateIcon
                  className={cn(styles.submitBtn, !shouldShowRegenerateBtn && styles.hidden)}
                  onClick={() => {
                    if (shouldShowRegenerateBtn) {
                      dispatch(
                        regenerateLastResponse(conversationID, conversation.targetContactID),
                      );
                    }
                  }}
                />
              </Tooltip>
            </span>
          ),
          nexusDisabled: (
            <div className={styles.upgradePrompt}>
              {enablementFailed ? (
                <span>
                  We were unable to enable Nexus for you. Please try again or{' '}
                  <a href={supportHref({ subject: 'Unable to enable Nexus' })}>contact us</a>.
                </span>
              ) : (
                <span>
                  Allow Nexus AI to access your data.{' '}
                  <a href="https://go.clay.earth/nexus-faq" target="_blank">
                    Learn more
                  </a>
                </span>
              )}

              <div
                className={cn(styles.enableBtn, currentlyEnabling && styles.enableBtnDisabled)}
                onClick={handleEnableNexusClick}
              >
                {currentlyEnabling ? (
                  <>
                    Enabling Nexus
                    <LoaderIcon />
                  </>
                ) : (
                  <>
                    Enable Nexus
                    <ArrowRightIcon color="textPrimary" />
                  </>
                )}
              </div>
            </div>
          ),
          refreshing: <span></span>,
        }}
      />
    </div>
  );
}
